import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Shield, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

export default function ReferralConfirm() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState("loading"); // loading|valid|invalid|done
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/referrals/public/${token}`);
        setInfo(data);
        setStatus(data.status === "pending" ? "valid" : "done");
      } catch (e) {
        setStatus("invalid");
      }
    })();
  }, [token]);

  const confirm = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/referrals/confirm/${token}`);
      setStatus("done");
      toast.success("Conferma ricevuta. Un consulente ti contatterà a breve!");
      setInfo({ ...info, status: data.status });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8F9] flex items-center justify-center p-6" data-testid="referral-confirm-page">
      <div className="w-full max-w-lg fade-up">
        <div className="flex items-center justify-center mb-8">
          <img src="/brand/besafe-logo.png" alt="Besafe Broker" className="h-16 w-auto"/>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 sm:p-10 text-center">
          {status === "loading" && (
            <>
              <Loader2 className="w-8 h-8 text-slate-400 mx-auto animate-spin"/>
              <div className="mt-4 text-slate-500">Caricamento…</div>
            </>
          )}

          {status === "invalid" && (
            <>
              <AlertTriangle className="w-12 h-12 text-[#F39C12] mx-auto"/>
              <h1 className="mt-4 font-display text-2xl text-[#0F7B85]">Link non valido</h1>
              <p className="mt-2 text-slate-600">Il link di conferma non è valido o è scaduto.</p>
              <Link to="/" className="inline-block mt-6 text-[#0F7B85] hover:text-[#F39C12] font-medium">Torna alla home</Link>
            </>
          )}

          {status === "valid" && info && (
            <>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Conferma segnalazione</div>
              <h1 className="mt-3 font-display text-3xl text-[#0F7B85] tracking-tight" data-testid="confirm-title">
                Ciao {info.friend_name.split(" ")[0]}!
              </h1>
              <p className="mt-3 text-slate-600 leading-relaxed">
                <strong>{info.customer_name}</strong> ti ha segnalato a Besafe Broker per ricevere un preventivo
                personalizzato sulla tua RCA{info.plate ? <> (targa <span className="font-mono uppercase text-[#0F7B85]">{info.plate}</span>)</> : null}.
              </p>
              <div className="mt-6 bg-[#F7F8F9] border border-slate-200 rounded-xl p-5 text-left text-sm text-slate-700 space-y-2">
                <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#0F7B85] mt-0.5 shrink-0"/> Ti contatteremo via email/telefono per un preventivo personalizzato.</div>
                <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#0F7B85] mt-0.5 shrink-0"/> Nessun obbligo di sottoscrizione.</div>
                <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#0F7B85] mt-0.5 shrink-0"/> Trattamento dei dati conforme al GDPR.</div>
              </div>
              <button
                onClick={confirm} disabled={submitting} data-testid="confirm-referral-button"
                className="mt-8 w-full bg-[#F39C12] text-white hover:bg-[#E08E10] disabled:opacity-60 rounded-lg px-6 py-3.5 font-medium transition-colors inline-flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
                Confermo, contattatemi
              </button>
              <p className="mt-4 text-xs text-slate-500">
                Confermando autorizzi Besafe Broker a contattarti per finalità di preventivazione assicurativa.
              </p>
            </>
          )}

          {status === "done" && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-50 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-700"/>
              </div>
              <h1 className="mt-4 font-display text-2xl text-[#0F7B85]">Conferma ricevuta!</h1>
              <p className="mt-2 text-slate-600">
                Grazie {info?.friend_name?.split(" ")[0]}. Un nostro consulente ti contatterà nei prossimi giorni.
              </p>
              <Link to="/" className="inline-block mt-6 text-[#0F7B85] hover:text-[#F39C12] font-medium">Vai alla home</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
